import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	CreateSecurityEventDto,
	CreateSecurityEventShape,
	UpdateSecurityEventDto,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/security.zod.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	createSecurityEvent,
	deleteSecurityEvent,
	updateSecurityEvent,
} from '@dealstack/frontend/app/services/securityEvent.service.ts';
import { SecurityEventRes, SecurityEventType } from '@dealstack/shared/lib/generated';
import { notifications } from '@mantine/notifications';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getPersonSecurities } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';

export enum CREATE_SECURITY_EVENT_FORM_FIELD {
	date = 'date',
	type = 'type',
	postEventUnits = 'postEventUnits',
	preEventUnits = 'preEventUnits',
	securityDetailsNominalValue = 'securityDetailsNominalValue',
	postEventSecurityId = 'postEventSecurityId',
	preEventNominalValue = 'preEventNominalValue',
}

export const useSecurityEventDrawerController = (
	refreshSecuritiesList: () => void,
	refreshSecurityNominalValue: () => void,
	onClose: (securityEventId?: string) => void,
	securityId?: string,
	entityId?: string,
	securityEvent?: SecurityEventRes
) => {
	const tCapManage = useTranslationCapManage();

	const [availableSecurities, setAvailableSecurities] = useState<
		{ value: string; label: string }[]
	>([]);

	const createSecurityEventRequest = useMutation({
		mutationFn: createSecurityEvent,
	});

	const updateSecurityEventRequest = useMutation({
		mutationFn: updateSecurityEvent,
	});

	const deleteSecurityEventRequest = useMutation({
		mutationFn: deleteSecurityEvent,
	});

	const getSecuritiesRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.SECURITY_PERSON_SECURITIES_PERSONID_POST, {
				personId: entityId || '',
			}),
		],

		queryFn: ({ signal }) =>
			getPersonSecurities(
				formatUnicorn(API_PATHS.SECURITY_PERSON_SECURITIES_PERSONID_POST, {
					personId: entityId || '',
				}),
				undefined,
				signal
			),
		enabled: !!entityId,
	});

	const DEFAULT_FORM_VALUES = useMemo(() => {
		return {
			[CREATE_SECURITY_EVENT_FORM_FIELD.date]: null,
			[CREATE_SECURITY_EVENT_FORM_FIELD.type]: null,
			[CREATE_SECURITY_EVENT_FORM_FIELD.securityDetailsNominalValue]: 0,
			preEventSecurityId: securityId || '',
			[CREATE_SECURITY_EVENT_FORM_FIELD.postEventSecurityId]: securityId,
			[CREATE_SECURITY_EVENT_FORM_FIELD.preEventNominalValue]: 0,
		} as unknown as CreateSecurityEventDto;
	}, [securityId]);

	const createSecurityEventForm = useForm<CreateSecurityEventDto>({
		mode: 'all',
		resolver: zodResolver(CreateSecurityEventShape),
		defaultValues: DEFAULT_FORM_VALUES,
		shouldFocusError: false,
	});

	const DEFAULT_EDIT_FORM_VALUES = useMemo(() => {
		return {
			[CREATE_SECURITY_EVENT_FORM_FIELD.date]: securityEvent?.date || null,
			[CREATE_SECURITY_EVENT_FORM_FIELD.type]: securityEvent?.type || null,
			[CREATE_SECURITY_EVENT_FORM_FIELD.preEventUnits]: Number(
				securityEvent?.preEventUnits || 0
			),
			[CREATE_SECURITY_EVENT_FORM_FIELD.postEventUnits]: Number(
				securityEvent?.postEventUnits || 0
			),
			[CREATE_SECURITY_EVENT_FORM_FIELD.preEventNominalValue]: Number(
				securityEvent?.preEventNominalValue || 0
			),
			[CREATE_SECURITY_EVENT_FORM_FIELD.securityDetailsNominalValue]: Number(
				securityEvent?.postEventNominalValue || 0
			),
			preEventSecurityId: securityEvent?.preEventSecurityId,
			[CREATE_SECURITY_EVENT_FORM_FIELD.postEventSecurityId]:
				securityEvent?.postEventSecurityId || securityEvent?.preEventSecurityId,
		} as unknown as CreateSecurityEventDto;
	}, [securityId, securityEvent]);

	const editSecurityEventForm = useForm<CreateSecurityEventDto>({
		mode: 'all',
		resolver: zodResolver(CreateSecurityEventShape),
		defaultValues: DEFAULT_EDIT_FORM_VALUES,
		shouldFocusError: false,
	});

	const handleSecurityTypeChange = useCallback(
		(newType: SecurityEventType) => {
			if (newType === SecurityEventType.CONVERSION) {
				const filteredSecurities = getSecuritiesRequest.data?.securities?.filter(
					(security) => security.id !== securityId
				);

				const securityOptions = filteredSecurities?.map((security) => ({
					value: security.id,
					label: security.name,
				}));

				setAvailableSecurities(securityOptions || []);
			}
		},
		[securityId, getSecuritiesRequest.data?.securities]
	);

	const getConversionPlaceholder = useCallback(() => {
		return availableSecurities.length === 0
			? tCapManage('securities.details.no_securities_available')
			: tCapManage('securities.details.select_security');
	}, [availableSecurities.length, tCapManage]);

	useEffect(() => {
		editSecurityEventForm.reset(DEFAULT_EDIT_FORM_VALUES);
	}, [DEFAULT_EDIT_FORM_VALUES, editSecurityEventForm]);

	useEffect(() => {
		if (securityEvent && securityEvent.type === SecurityEventType.CONVERSION) {
			const filteredSecurities = getSecuritiesRequest.data?.securities?.filter(
				(security) => security.id !== securityId
			);

			const securityOptions = filteredSecurities?.map((security) => ({
				value: security.id,
				label: security.name,
			}));

			setAvailableSecurities(securityOptions || []);
		}
	}, [securityEvent, securityId, getSecuritiesRequest.data?.securities]);

	const buildUnitsAndNominalValues = (
		type: SecurityEventType,
		preEventUnits?: number,
		postEventUnits?: number,
		securityDetailsNominalValue?: number,
		preEventNominalValue?: number
	): {
		preEventUnits?: number;
		postEventUnits?: number;
		preEventNominalValue?: number;
		postEventNominalValue?: number;
	} => {
		const calculatedValues = {
			preEventUnits: undefined as number | undefined,
			postEventUnits: undefined as number | undefined,
			postEventNominalValue: undefined as number | undefined,
			preEventNominalValue,
		};

		switch (type) {
			case SecurityEventType.SPLIT:
				calculatedValues.preEventUnits = 1;
				calculatedValues.postEventUnits = postEventUnits;
				break;

			case SecurityEventType.REVERSE_SPLIT:
				calculatedValues.preEventUnits = preEventUnits;
				calculatedValues.postEventUnits = 1;
				break;

			case SecurityEventType.ADJUST:
				calculatedValues.preEventUnits = 1;
				calculatedValues.postEventUnits = 1;
				calculatedValues.postEventNominalValue = securityDetailsNominalValue;
				break;

			case SecurityEventType.CONVERSION:
				calculatedValues.preEventUnits = 1;
				calculatedValues.postEventUnits = 1;
				break;

			default:
				break;
		}

		return calculatedValues;
	};

	const onSubmit = () => (formValues: CreateSecurityEventDto) => {
		if (!securityId) {
			return;
		}

		const { preEventUnits, postEventUnits, preEventNominalValue, postEventNominalValue } =
			buildUnitsAndNominalValues(
				formValues.type!,
				formValues.preEventUnits,
				formValues.postEventUnits,
				formValues.securityDetailsNominalValue,
				formValues.preEventNominalValue
			);

		const payload: CreateSecurityEventDto = {
			date: formValues[CREATE_SECURITY_EVENT_FORM_FIELD.date],
			preEventSecurityId: securityId,
			type: formValues[CREATE_SECURITY_EVENT_FORM_FIELD.type],
			preEventUnits,
			postEventUnits,
			postEventNominalValue,
			preEventNominalValue,
			postEventSecurityId: formValues.postEventSecurityId,
		};

		createSecurityEventRequest.mutate(payload, {
			onSuccess: async () => {
				createSecurityEventForm.reset({ ...DEFAULT_FORM_VALUES });
				onClose();
				refreshSecuritiesList();
				refreshSecurityNominalValue();
				showToastV2({
					message: tCapManage('securities.details.event_created'),
				});
			},
			onError: () => {
				notifications.cleanQueue();
				notifications.clean();

				showToastV2({
					state: 'error',
					message: tCapManage('securities.details.spit_reverse_same_day'),
				});
			},
		});
	};

	const onEditSubmit = () => (formValues: UpdateSecurityEventDto) => {
		if (!securityId) {
			return;
		}

		if (!securityEvent?.id || securityEvent.id.trim() === '') {
			throw new Error('Cannot update security event: Missing event ID.');
		}

		const { preEventUnits, postEventUnits, postEventNominalValue } = buildUnitsAndNominalValues(
			formValues.type!,
			formValues.preEventUnits,
			formValues.postEventUnits,
			formValues.securityDetailsNominalValue
		);

		let finalPostEventSecurityId = formValues.postEventSecurityId;

		if (formValues.type !== SecurityEventType.CONVERSION) {
			finalPostEventSecurityId = securityEvent?.preEventSecurityId;
		}

		const payload: UpdateSecurityEventDto = {
			id: securityEvent?.id || '',
			date: formValues[CREATE_SECURITY_EVENT_FORM_FIELD.date],
			preEventSecurityId: securityEvent?.preEventSecurityId,
			type: formValues[CREATE_SECURITY_EVENT_FORM_FIELD.type],
			preEventUnits,
			postEventUnits,
			postEventNominalValue,
			postEventSecurityId: finalPostEventSecurityId,
		};

		updateSecurityEventRequest.mutate(payload, {
			onSuccess: async () => {
				updateSecurityEventRequest.reset();
				onClose();
				refreshSecuritiesList();
				showToastV2({
					message: tCapManage('securities.details.event_edited'),
				});
			},
		});
	};

	const onDelete = useCallback(
		async (securityEventId: string) => {
			return deleteSecurityEventRequest.mutateAsync(securityEventId).then(() => {
				refreshSecuritiesList();
				showToastV2({
					message: tCapManage('securities.details.event_deleted'),
				});
			});
		},
		[deleteSecurityEventRequest, refreshSecuritiesList, tCapManage]
	);

	return {
		createSecurityEventRequest,
		onSubmit,
		onEditSubmit,
		onDelete,
		createSecurityEventForm,
		updateSecurityEventRequest,
		editSecurityEventForm,
		securities: getSecuritiesRequest.data?.securities,
		availableSecurities,
		getConversionPlaceholder,
		handleSecurityTypeChange,
	};
};
